<template>
  <b-modal
    id="chat-agent-modal"
    ref="chatAgentModal"
    :title="
      isEdit ? $t('title.editChatAgent') : $t('breadcrumb.chatAgentConfigure')
    "
    size="lg"
    hide-footer
    content-class="shadow"
    no-close-on-backdrop
    @hide="onModalHide"
  >
    <validation-observer ref="agentForm" v-slot="{ invalid }">
      <b-form @submit.prevent="validateAndSubmit">
        <!-- Agent Name -->
        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.title')"
          :rules="
            locale === $i18n.locale.split('-')[0]
              ? 'required|min:3|max:47'
              : 'min:3|max:47'
          "
        >
          <b-form-group :label="$t('field.title')">
            <div class="d-flex" style="gap: 10px;">
                <b-form-input
                  v-model="agent.name[locale]"
                  :placeholder="$t('placeholder.title')"
                  :state="errors.length > 0 ? false : null"
                />
                <b-form-select
                  v-model="locale"
                  :options="locales"
                  :style="{
                    width: 'auto',
                  }"
                />
            </div>
            <small class="text-danger" :style="{ paddingTop: '5px' }">{{
              formatErrorMessage(errors[0])
            }}</small>
          </b-form-group>
        </validation-provider>
        <b-col />

        <!-- Agent Task -->
        <validation-provider
          v-slot="{ errors }"
          :name="$t('field.ability')"
          :rules="validationRules"
        >
          <b-form-group :label="$t('field.ability')">
            <div class="d-flex" style="gap: 10px;">
                <b-form-textarea
                  v-model="agent.ability[locale]"
                  :placeholder="$t('placeholder.abilityDescription')"
                  rows="3"
                  :state="errors.length > 0 ? false : null"
                />
                <b-form-select
                  v-model="locale"
                  :options="locales"
                  :style="{
                    width: 'auto',
                  }"
                />
            </div>
            <small class="text-danger">{{
              formatErrorMessage(errors[0])
            }}</small>
          </b-form-group>
        </validation-provider>

        <!-- Service System Section -->
        <b-form-group :label="$t('field.serviceSystem')">
          <b-form-checkbox
            v-model="agent.isServiceSystemEnabled"
            :value="true"
            :unchecked-value="false"
            @change="handleServiceSystemChange"
          >
            {{ $t('button.enable') }}
          </b-form-checkbox>

          <!-- Service System Preview -->
          <div
            class="service-preview mt-2"
            v-if="agent.isServiceSystemEnabled && agent.projects.length"
          >
            <div
              class="preview-header d-flex justify-content-between align-items-center mb-1"
            >
              <h6 class="mb-0">{{ $t('title.serviceSystemSettings') }}</h6>
              <b-button
                variant="link"
                class="p-0"
                @click="handleEditServiceSystem"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
            </div>

            <!-- Service Info Grid -->
            <div class="service-info">
              <b-row class="mb-1">
                <!-- Projects Column -->
                <b-col md="6">
                  <div class="d-flex">
                    <div class="font-weight-bold" style="width: 120px">
                      {{ $t('breadcrumb.project') }}:
                    </div>
                    <div class="flex-grow-1">
                      <div
                        v-for="project in agent.projects"
                        :key="project.id"
                        class="mb-1"
                      >
                        {{ projectLabel(project) }}
                      </div>
                    </div>
                  </div>
                </b-col>

                <!-- Service Category Column -->
                <b-col md="6">
                  <div class="d-flex">
                    <div class="font-weight-bold" style="width: 120px">
                      {{ $t('field.category') }}:
                    </div>
                    <div>
                      {{
                        getCategoryLabel(agent.serviceCategory)
                      }}
                    </div>
                  </div>
                </b-col>
              </b-row>

              <b-row>
                <!-- Service Type Column -->
                <b-col md="6">
                  <div class="d-flex">
                    <div class="font-weight-bold" style="width: 120px">
                      {{ $t('field.type') }}:
                    </div>
                    <div>
                      {{
                        getServiceTypeLabel(agent.serviceType)
                      }}
                    </div>
                  </div>
                </b-col>

                <!-- Service Sub Type Column -->
               <!--  <b-col md="6">
                  <div class="d-flex">
                    <div class="font-weight-bold" style="width: 120px">
                      {{ $t('field.subType') }}:
                    </div>  
                    <div>
                      {{
                        agent.serviceSubType && agent.serviceSubType.name
                          ? agent.serviceSubType.name[locale]
                          : '-'
                      }}
                    </div>
                  </div>
                </b-col> -->
              </b-row>

              <!-- <b-row>
                <b-col md="6">
                  <div class="d-flex">
                    <div class="font-weight-bold" style="width: 120px">
                      {{ $t('field.subType') }}:
                    </div>
                    <div>
                      {{
                        agent.serviceSubType && agent.serviceSubType.name
                          ? agent.serviceSubType.name[locale]
                          : '-'
                      }}
                    </div>
                  </div>
                </b-col>
              </b-row> -->
            </div>
          </div>
        </b-form-group>

        <!-- Steps Section with Draggable -->
        <b-form-group :label="$t('field.workflowSteps')" class="mt-2">
          <draggable
            v-model="agent.steps"
            class="step-list"
            ghost-class="ghost"
            :animation="200"
            draggable=".item"
            :style="stepListPaddingLeft"
            @start="isDragging = true"
            @end="isDragging = false"
          >
            <div
              v-for="(step, index) in agent.steps"
              :key="index"
              class="step-card item position-relative mt-1"
            >
              <!-- Step Number Badge -->
              <div class="step-number">
                <span>{{ index + 1 }}</span>
              </div>

              <!-- Vertical Line - Only show if not the last step and not dragging -->
              <div
                v-if="!isDragging && index !== agent.steps.length - 1"
                class="step-connector"
              />

              <b-row class="d-flex align-items-start">
                <b-col>
                  <h6 class="text-primary">
                    {{ step.name }}
                  </h6>
                  <p
                    v-html="
                      step.description
                        ? step.description.replace(/\n/g, '<br />')
                        : ''
                    "
                  />

                  <!-- Question Type Display -->
                  <div
                    class="d-flex align-items-center"
                    :style="{ marginBottom: '5px' }"
                  >
                    <span class="text-bold"
                      >{{ $t('field.questionType') }}:
                    </span>
                    &nbsp;
                    <b-badge variant="info">
                      {{
                        getQuestionTypeLabel(
                          step.questionType || QUESTION_TYPE.OPEN
                        )
                      }}
                    </b-badge>
                  </div>

                  <!-- Answers Display for Multiple Choice -->
                  <!-- <template v-if="step.questionType !== QUESTION_TYPE.OPEN && step.answers && step.answers.length">
                    <div class="d-flex align-items-center flex-wrap gap-1 mb-2">
                      <span class="text-bold">{{ $t('field.answers') }}: </span>
                      &nbsp;
                      <b-badge
                        v-for="(answer, answerIndex) in step.answers"
                        :key="answerIndex"
                        variant="secondary"
                        class="mr-1"
                      >
                        {{ answer.text }}
                      </b-badge>
                    </div>
                  </template> -->

                  <template v-if="step.parameters && step.parameters.length && step.questionType !== QUESTION_TYPE.MULTI_CHOICE_FOR_PRODUCT">
                    <div
                      class="d-flex align-items-center flex-wrap gap-1"
                      :style="{ marginBottom: '5px' }"
                    >
                      <span class="text-bold"
                        >{{ $t('field.parameters') }}:
                      </span>
                      &nbsp;
                      <b-badge
                        v-for="(parameter, paramIndex) in step.parameters"
                        :key="paramIndex"
                        style="margin-top: 4px; margin-bottom: 4px;"
                        variant="primary"
                        class="mr-1"
                      >
                        {{ getParameterName(parameter) }} ({{ parameter.key }})
                      </b-badge>
                    </div>
                  </template>
                  <div>
                    <span class="text-bold"
                      >{{ $t('field.callbackUrl') }}: &nbsp;</span
                    >
                    <b-link style="word-break: break-all;">{{ step.callbackUrl }}</b-link>
                  </div>
                </b-col>
                <b-col cols="auto">
                  <b-button-group class="mr-1">
                    <b-button
                      v-b-tooltip.hover
                      class="mr-1"
                      :title="$t('general.update')"
                      variant="primary"
                      pill
                      size="sm"
                      @click="handleUpdateStep(step, index)"
                    >
                      <feather-icon icon="EditIcon" />
                    </b-button>
                    <b-button
                      v-b-tooltip.hover
                      :title="$t('general.delete')"
                      variant="danger"
                      pill
                      size="sm"
                      @click="removeStep(index)"
                    >
                      <feather-icon icon="TrashIcon" />
                    </b-button>
                  </b-button-group>
                </b-col>
              </b-row>
            </div>

            <!-- Add Step Card -->
            <div
              slot="footer"
              key="footer"
              class="step-card add-step-card mb-1"
              @click="showStepModal()"
            >
              <div class="d-flex h-100 align-items-center flex-column">
                <div class="d-flex align-items-center mb-1">
                  <feather-icon icon="PlusIcon" />
                  <span
                    class="text-primary text-bold ml-1"
                    :style="{ fontSize: '16px' }"
                  >
                    {{ $t('button.addStep') }}
                  </span>
                </div>
                <span class="text-gray">{{ $t('stepGuide') }}</span>
              </div>
            </div>
          </draggable>
        </b-form-group>

        <b-form-group v-if="isEdit" :label="$t('field.status')" class="mt-2">
          <b-form-radio-group
            v-model="agent.isEnabled"
            :label="$t('field.status')"
            buttons
            button-variant="outline-primary rounded-0"
            class="flex-wrap"
          >
            <b-form-radio
              v-for="option in activeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ $t(option.text) }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <!-- Form Actions -->
        <b-row class="mt-3">
          <b-col class="text-right">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="closeModal"
            >
              {{ $t('button.cancel') }}
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              :disabled="invalid || loading || agent.steps.length === 0 || !hasAnyNameLanguage || !hasAnyAbilityLanguage"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{ isEdit ? $t('button.update') : $t('button.save') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <!-- Step Modal -->
    <step-form-modal
      ref="stepModal"
      :show="isShowStepModal"
      :selected-step="currentStep"
      :is-edit="isEditingStep"
      @hide-modal="isShowStepModal = false"
      @save-step="step => onStepSave(step)"
      @update-step="step => onStepUpdate(step)"
    />

    <service-system-modal
      ref="serviceSystemModal"
      :show="showServiceSystemModal"
      :edit-data="editServiceSystem"
      @hide="handleServiceSystemCancel"
      @submit="handleServiceSystemSubmit"
      @cancel="handleServiceSystemCancel"
    />
  </b-modal>
</template>

<script>
  import {
    BRow,
    BCol,
    BCard,
    BCardText,
    BLink,
    BModal,
    BButton,
    BButtonGroup,
    BForm,
    BFormInput,
    BFormGroup,
    BFormTextarea,
    BFormCheckbox,
    BFormSelect,
    BBadge,
    BSkeletonTable,
    BFormRadioGroup,
    BFormRadio
  } from 'bootstrap-vue';
  import Ripple from 'vue-ripple-directive';
  import NButtonLoading from '@/components/NButtonLoading.vue';
  import NFormConfirmation from '@/components/NFormConfirmation.vue';
  import NInput from '@/components/NInput.vue';
  import NSingleSelect from '@/components/NSingleSelect.vue';
  import Repository from '@/repositories/RepositoryFactory';
  import NAsyncSingleSelect from '@/components/NAsyncSingleSelect';
  import NMultiSelect from '@/components/NMultiSelect';
  // import './validateRules';
  import draggable from 'vuedraggable';
  import StepFormModal from './StepFormModal.vue';
  import FormInput from './formInput';
  import ServiceSystemModal from './ServiceSystemModal.vue';

  const ChatAgentRegistry = Repository.get('chatAgent');

  export default {
    components: {
      BRow,
      BCol,
      BCard,
      BCardText,
      BLink,
      BModal,
      BButtonGroup,
      BButton,
      BForm,
      NButtonLoading,
      NFormConfirmation,
      NInput,
      NSingleSelect,
      BFormInput,
      BFormGroup,
      BFormTextarea,
      BFormCheckbox,
      BFormSelect,
      NAsyncSingleSelect,
      NMultiSelect,
      draggable,
      BBadge,
      StepFormModal,
      BSkeletonTable,
      BFormRadioGroup,
      BFormRadio,
      ServiceSystemModal
    },
    directives: {
      Ripple
    },
    props: {
      show: {
        type: Boolean,
        required: true
      },
      editData: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        agent: {
          id: null,
          name: {
            en: '',
            zh: '',
            vi: '',
            km: ''
          },
          ability: {
            en: '',
            zh: '',
            vi: '',
            km: ''
          },
          steps: [],
          isServiceSystemEnabled: false,
          serviceSystemParameters: [],
          projects: [],
          serviceCategory: null,
          serviceType: null,
          isEnabled: true,
          isTemplateSteps: false
        },
        currentStep: null,
        isEditingStep: false,
        loading: false,
        isEdit: false,
        dragOptions: {
          animation: 200,
          handle: '.drag-handle',
          ghostClass: 'ghost'
        },
        isShowStepModal: false,
        selectedStepIndex: null,
        locales: [
          { value: 'en', text: 'English' },
          { value: 'zh', text: '中文' },
          { value: 'km', text: 'ខ្មែរ' },
          { value: 'vi', text: 'Tiếng Anh' }
        ],
        locale: this.$i18n.locale.split('-')[0],
        activeOptions: [
          { text: 'status.active', value: true },
          { text: 'status.inactive', value: false }
        ],
        isDragging: false,
        showServiceSystemModal: false,
        editServiceSystem: null,
        serviceSubType: null,
        serviceSubTypeCode: null,
        QUESTION_TYPE: {
          OPEN: 1,
          MULTI_CHOICE_SINGLE_ANSWER: 2,
          MULTI_CHOICE_MULTI_ANSWER: 3,
          MULTI_CHOICE_FOR_PRODUCT: 6,
          ATTACHMENTS: 7
        },
        stepTemplate: null
      };
    },
    computed: {
      availableParameters() {
        // Get parameters and filter out empty values
        const parameters = this.agent.steps
          .flatMap(step => step.parameters)
          .filter(param => param.key && param.name) // Filter out empty keys or names
          ?.map(param => ({
            value: param.key,
            text: param.name
          }));
        // Remove duplicates based on value (key)
        return Array.from(
          new Map(parameters?.map(item => [item.value, item])).values()
        );
      },
      isFormValid() {
        if (this.agent.isServiceSystemEnabled) {
          return this.agent.projects?.length > 0;
        }
        return true;
      },
      stepListPaddingLeft() {
        return {
          paddingLeft: this.agent.steps.length > 0 ? '40px' : '0'
        };
      },
      validationRules() {
        const currentLocale = this.$i18n.locale.split('-')[0];
        const isCurrentLocale = this.locale === currentLocale;
        const minLength = this.locale === 'zh' ? 5 : 10;

        const rules = {
          max: 300,
          min: minLength
        };

        if (isCurrentLocale) {
          rules.required = true;
        }

        return Object.entries(rules)
          ?.map(([rule, value]) => {
            if (value === true) return rule;
            return `${rule}:${value}`;
          })
          .join('|');
      },
      localeForTranslation() {
        return this.$i18n.locale.split('-')[0];
      },
      hasAnyNameLanguage() {
        const names = this.agent?.name || {};
        return Object.values(names).some(v => (v || '').toString().trim().length > 0);
      },
      hasAnyAbilityLanguage() {
        const abilities = this.agent?.ability || {};
        return Object.values(abilities).some(v => (v || '').toString().trim().length > 0);
      }
    },
    watch: {
      show(val) {
        if (val) {
          this.showModal();
          if (this.editData.id) {
            this.initializeEditData();
          }
        } else {
          this.reset();
        }
      },
      editData(val) {
        if (val.id && this.show) {
          this.initializeEditData();
        }
      },
      '$i18n.locale': function (val) {
        this.locale = val.split('-')[0];
      }
    },
    mounted() {
      this.getStepTemplate();
    },
    methods: {
      getParameterName(parameter) {
        return parameter?.nameTranslate?.[this.localeForTranslation] || parameter?.nameTranslate?.en || parameter?.nameTranslate?.zh || parameter?.nameTranslate?.km || parameter?.nameTranslate?.vi || parameter?.name || '-';
      },
      projectLabel(project) {
        if (project?.projectId === 'ALL_PROJECT' || project?.id === 'ALL_PROJECT') {
          return this.$t('general.all')
        }

        if (Object.keys(project?.translationsObj || {}).length > 0) {
          return project?.translationsObj?.[this.localeForTranslation] || project?.translationsObj?.en || project?.translationsObj?.zh || project?.translationsObj?.km || project?.translationsObj?.vi || '-';
        }

        return project?.name;
      },
      getCategoryLabel(category) {
        return category?.name?.[this.localeForTranslation] || category?.name?.en || category?.name?.zh || category?.name?.km || category?.name?.vi || '-';
      },
      getServiceTypeLabel(serviceType) {
        return serviceType?.name?.[this.localeForTranslation] || serviceType?.name?.en || serviceType?.name?.zh || serviceType?.name?.km || serviceType?.name?.vi || '-';
      },
      getLabel(option) {
        return option?.name || '';
      },
      initializeEditData() {
        this.isEdit = true;
        this.agent = {
          ...this.editData,
          steps:
            this.editData.steps?.map(step => ({
              ...step,
              parameters: step.parameters?.map(param => ({ ...param })),
              callbackUrl: step.callbackUrl
            })) || [],
          isServiceSystemEnabled: this.editData.isServiceSystemEnabled || false,
          serviceSystemParameters: this.editData.serviceSystemParameters || [],
          name: {
            en: this.editData.name.en || '',
            zh: this.editData.name.zh || '',
            vi: this.editData.name.vi || '',
            km: this.editData.name.km || ''
          },
          ability: {
            en: this.editData.ability?.en || '',
            zh: this.editData.ability?.zh || '',
            vi: this.editData.ability?.vi || '',
            km: this.editData.ability?.km || ''
          },
          projects: this.editData.projects || [],
          serviceType: this.editData.serviceType || null,
          serviceCategoryCode: this.editData.serviceCategory?.code || null,
          serviceTypeCode: this.editData.serviceType?.code || null,
          projectIds: this.editData.projects?.map(project => project?.projectId)
        };
        
        // Check if the service type is a template type and set isTemplateSteps accordingly
        if (
          this.agent.serviceTypeCode &&
          ['LAUNDRY_ORDER', 'CLEANING_ORDER', 'WATER_DELIVERY_ORDER'].includes(
            this.agent.serviceTypeCode
          )
        ) {
          this.agent.isTemplateSteps = true;
        } else {
          this.agent.isTemplateSteps = false;
        }
      },
      createDefaultStep() {
        return {
          name: '',
          description: '',
          parameters: [this.createDefaultParameter()],
          callbackUrl: '',
          questionType: this.QUESTION_TYPE.OPEN,
          answers: []
        };
      },
      createDefaultParameter() {
        return {
          name: '',
          key: ''
        };
      },
      addStep() {
        // this.agent.steps.push(this.createDefaultStep())
      },
      removeStep(index) {
        if (this.agent.steps.length > 1) {
          this.agent.steps.splice(index, 1);
        } else {
          this.$bvToast.toast(this.$t('message.atLeastOneStep'), {
            variant: 'warning'
          });
        }
      },
      addParameter(step) {
        step.parameters.push(this.createDefaultParameter());
      },
      removeParameter(step, paramIndex) {
        if (step.parameters.length > 1) {
          step.parameters.splice(paramIndex, 1);
        } else {
          this.$bvToast.toast(this.$t('message.atLeastOneParameter'), {
            variant: 'warning'
          });
        }
      },
      validateAndSubmit() {
        this.$refs.agentForm.validate().then(success => {
          if (success) {
            if (this.isEdit) {
              this.update();
            } else {
              this.create();
            }
          }
        });
      },
      closeModal() {
        this.$refs.chatAgentModal.hide();
        this.reset();
        this.$emit('hide-modal');
      },
      showModal() {
        this.$refs.chatAgentModal.show();
      },
      async create() {
        this.loading = true;
        try {
          if (!this.agent.isServiceSystemEnabled) {
            delete this.agent.serviceSystemParameters;
            delete this.agent.serviceSystemDepartmentId;
            delete this.agent.serviceCategory;
            delete this.agent.serviceType;
            delete this.agent.serviceSubType;
            delete this.agent.serviceSubTypeCode;
          }

          let projects = [];

          if (this.agent.projects?.length) {
            projects = this.agent.projects?.map(project => ({
              projectId: project.id,
              name: project.name,
              translation: project.translation
            }));
          }
          const response = await ChatAgentRegistry.create({
            ...this.agent,
            projects
          });
          const { code } = response.data;
          if (code === 200) {
            this.$emit('created');
            this.closeModal();
          }
        } catch (error) {
          console.error('Error creating chat agent:', error);
          // this.$bvToast.toast(this.$t('message.createError'), {
          //   variant: 'danger'
          // });
        } finally {
          this.loading = false;
        }
      },
      async update() {
        this.loading = true;
        if (!this.agent.isServiceSystemEnabled) {
          delete this.agent.serviceSystemParameters;
          delete this.agent.serviceSystemDepartmentId;
          delete this.agent.serviceCategory;
          delete this.agent.serviceType;
          delete this.agent.serviceSubType;
          delete this.agent.serviceSubTypeCode;
        }

        let projects = [];

        if (this.agent.projects?.length) {
          projects = this.agent.projects?.map(project => ({
            projectId: project?.projectId ? project?.projectId : project?.id,
            name: project.name,
            translation: project.translation
          }));
        }
        try {
          const response = await ChatAgentRegistry.update(this.agent.id, {
            ...this.agent,
            projects
          });
          const { code } = response.data;
          if (code === 200) {
            this.$emit('created');
            this.closeModal();
          }
        } catch (error) {
          console.error('Error updating chat agent:', error);
          // this.$bvToast.toast(this.$t('message.updateError'), {
          //   variant: 'danger'
          // });
        } finally {
          this.loading = false;
        }
      },
      onModalHide() {
        this.$emit('hide-modal');
      },
      reset() {
        this.agent = {
          id: null,
          name: {
            en: '',
            zh: '',
            vi: '',
            km: ''
          },
          ability: {
            en: '',
            zh: '',
            vi: '',
            km: ''
          },
          steps: [],
          isServiceSystemEnabled: false,
          serviceSystemParameters: [],
          projects: [],
          serviceCategory: null,
          serviceType: null,
          isEnabled: true,
          isTemplateSteps: false
        };
        this.isEdit = false;
        this.currentStep = null;
        this.isEditingStep = false;
        this.loading = false;
        this.isShowStepModal = false;
        this.selectedStepIndex = null;
        this.locale =
          this.$i18n.locale.split('-')[0] === 'km' ||
          this.$i18n.locale.split('-')[0] === 'vi'
            ? 'en'
            : this.$i18n.locale.split('-')[0];
        this.editServiceSystem = null;
        this.agent.serviceSubType = null;
        this.agent.serviceSubTypeCode = null;
      },
      formatErrorMessage(error) {
        if (!error) return '';

        // Remove field name that contains numbers (e.g., "param0Key", "param01Key")
        return error
          .replace(/param\d+Key/g, 'parameter key')
          .replace(/param\d+\d+Key/g, 'parameter key');
      },
      showStepModal(step = null) {
        this.currentStep = step
          ? {
              ...step,
              answers: Array.isArray(step.answers) ? step.answers : [],
              questionType: step.questionType || this.QUESTION_TYPE.OPEN
            }
          : this.createDefaultStep();
        this.isEditingStep = !!step;
        this.isShowStepModal = true;
      },
      removeStep(index) {
        this.agent.steps.splice(index, 1);
      },
      onStepSave(step) {
        this.agent.steps.push(step);
      },
      onStepUpdate(step) {
        const index = this.selectedStepIndex;
        delete step.index;
        this.$set(this.agent.steps, index, step);
      },
      handleUpdateStep(step, index) {
        this.selectedStepIndex = index;
        this.currentStep = step;
        this.isEditingStep = true;
        this.isShowStepModal = true;
      },
      handleServiceSystemChange(value) {
        if (value) {
          // Clear existing steps when initially enabling service system
          if (!this.agent.isServiceSystemEnabled) {
            this.agent.steps = [];
          }
          this.showServiceSystemModal = true;
          this.$nextTick(() => {
            this.$refs.serviceSystemModal?.show();
          });
        }
      },
      handleServiceSystemSubmit(data) {
        const prevServiceTypeCode = this.agent.serviceTypeCode;
        this.agent.isServiceSystemEnabled = true;
        this.agent.projects = data.projects || [];
        this.agent.serviceCategory = data.serviceCategory;
        this.agent.serviceType = data.serviceType;
        this.agent.serviceCategoryCode = data.serviceCategory?.code;
        this.agent.serviceTypeCode = data.serviceType?.code;
        this.agent.serviceSubType = data.serviceSubType;
        this.agent.serviceSubTypeCode = data.serviceSubType?.code;

        // Initialize or clear steps based on service type code
        if (this.agent.isServiceSystemEnabled && this.agent.serviceTypeCode) {
          const typeCode = this.agent.serviceTypeCode;
          if (
            [
              'LAUNDRY_ORDER',
              'CLEANING_ORDER',
              'WATER_DELIVERY_ORDER'
            ].includes(typeCode)
          ) {
            // In create mode, initialize steps from template; skip when editing
            if (!this.isEdit) {
              this.agent.steps = JSON.parse(
                JSON.stringify(this.stepTemplate?.[typeCode])
              );
            }
            this.agent.isTemplateSteps = true;
          } else if (
            (this.isEdit &&
              [
                'LAUNDRY_ORDER',
                'CLEANING_ORDER',
                'WATER_DELIVERY_ORDER'
              ].includes(this.editData.serviceType?.code) &&
              ![
                'LAUNDRY_ORDER',
                'CLEANING_ORDER',
                'WATER_DELIVERY_ORDER'
              ].includes(typeCode)) ||
            (!this.isEdit &&
              [
                'LAUNDRY_ORDER',
                'CLEANING_ORDER',
                'WATER_DELIVERY_ORDER'
              ].includes(prevServiceTypeCode))
          ) {
            // Clear steps for non-template service types
            this.agent.steps = [];
            this.agent.isTemplateSteps = false;
          }
        }

        this.showServiceSystemModal = false;
      },
      handleEditServiceSystem() {
        this.editServiceSystem = {
          projects: this.agent.projects.map(project => ({
            ...project,
            projectId: project?.projectId ? project?.projectId : project?.id
          })),
          projectIds: this.agent.projects?.length
            ? this.agent.projects?.map(project =>
                project?.projectId ? project?.projectId : project?.id
              )
            : [],
          serviceCategory: this.agent.serviceCategory,
          serviceType: this.agent.serviceType,
          serviceCategoryCode: this.agent.serviceCategory?.code,
          serviceTypeCode: this.agent.serviceType?.code,
          serviceSubType: this.agent.serviceSubType,
          serviceSubTypeCode: this.agent.serviceSubType?.code,
          existingSteps: this.agent.isTemplateSteps ? [] : this.agent.steps
        };

        this.showServiceSystemModal = true;
        this.$nextTick(() => {
          this.$refs.serviceSystemModal?.show();
        });
      },
      handleServiceSystemCancel() {
        this.showServiceSystemModal = false;
        if (!this.agent.projects?.length) {
          this.agent.isServiceSystemEnabled = false;
          this.agent.projects = [];
          this.agent.serviceCategory = null;
          this.agent.serviceType = null;
          this.agent.serviceCategoryCode = null;
          this.agent.serviceTypeCode = null;
          this.agent.projectIds = [];
          this.agent.isTemplateSteps = false;
          this.editServiceSystem = null;
          this.agent.serviceSubType = null;
          this.agent.serviceSubTypeCode = null;
        }
      },
      getNameByLang(data) {
        if (!data?.name) return '';
        return data.name?.[this.localeForTranslation] || data.name?.zh || data.name?.en || data.name?.km || data.name?.vi || ''
      },
      getQuestionTypeLabel(type) {
        switch (type) {
          case this.QUESTION_TYPE.OPEN:
            return this.$t('field.openQuestion');
          case this.QUESTION_TYPE.MULTI_CHOICE_SINGLE_ANSWER:
            return this.$t('field.singleChoice');
          case this.QUESTION_TYPE.MULTI_CHOICE_MULTI_ANSWER:
            return this.$t('field.multipleChoice');
          case this.QUESTION_TYPE.MULTI_CHOICE_FOR_PRODUCT:
            return this.$t('field.multipleChoiceForProduct');
          case this.QUESTION_TYPE.ATTACHMENTS:
            return this.$t('field.attachments');
          default:
            return this.$t('field.openQuestion');
        }
      },
      getStepTemplate() {
        ChatAgentRegistry.getStepTemplate().then(res => {
          this.stepTemplate = res.data.data;
        });
      }
    },
    setup() {
      const fieldsCommand = FormInput;
      return {
        fields: fieldsCommand?.map(e => ({ ...e }))
      };
    }
  };
</script>

<style lang="scss" scoped>
  .step-list {
    min-height: 50px;
    border-radius: 8px;
    position: relative;
  }

  .step-card {
    background-color: white;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 15px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    cursor: move;
    position: relative;
    margin-bottom: 20px;

    &:hover {
      background-color: #f8f9fa;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
  }

  .step-connector {
    position: absolute;
    left: -25px;
    top: 30px;
    width: 2px;
    height: calc(100% + 20px);
    background-color: #dee2e6;
    z-index: 1;
  }

  .step-number {
    position: absolute;
    left: -40px;
    top: 15px;
    width: 30px;
    height: 30px;
    background-color: #8d5185;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    z-index: 2;
  }

  .add-step-card {
    border: 2px dashed #dee2e6;
    background-color: #f8f9fa;
    cursor: pointer;
    transition: all 0.3s ease;
    height: 100px;

    &:hover {
      border-color: #8d5185;
      background-color: #f0f7ff;
    }
  }

  .ghost {
    opacity: 0.5;
    background: #e9ecef;
    border: 2px dashed #8d5185;
  }

  .button-group {
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .step-card:hover .button-group {
    opacity: 1;
  }

  .service-preview {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    padding: 0.75rem;
  }

  .preview-header {
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #dee2e6;

    h6 {
      color: #495057;
      font-weight: 600;
    }
  }

  .group-item {
    padding: 0.5rem;
    border-bottom: 1px dashed #dee2e6;

    &:last-child {
      border-bottom: none;
    }
  }

  .group-number {
    background: #e9ecef;
    color: #495057;
    width: 20px;
    height: 20px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .group-detail {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.25rem;

    i {
      width: 16px;
    }
  }
</style>
